<template>
  <template v-if="column.formatter === 'kda'">
    <span :class="getStatColorClassForCell()">
      {{ formatKDA(player.kills.value, player.deaths.value, player.assists.value) }}
    </span>
  </template>
  <template v-else-if="column.key === 'kd_ratio'">
    <StatBreakdown stat-key="kdr" source="match" :values="player">
      <span :class="getStatColorClassForCell()">
        {{ calculateKDRatio(player.kills.value, player.deaths.value) }}
      </span>
    </StatBreakdown>
  </template>
  <template v-else>
    <StatBreakdown :stat-key="column.key" source="match" :values="player">
      <span :class="getStatColorClassForCell()">
        {{ formatStatValue(player[column.key]?.value, column.formatter, column) }}
      </span>
    </StatBreakdown>
  </template>
</template>

<script setup>
import { calculateKDRatio, formatKDA, formatDataString } from '../../../../utils/formatters.js';
import { isWorseWhenHigher as isWorseWhenHigherStat } from '../../../../utils/stat_directions.js';
import StatBreakdown from '../../StatBreakdown.vue';

const props = defineProps({
  player: {
    type: Object,
    required: true,
  },
  column: {
    type: Object,
    required: true,
  },
});

// Simple formatter function that uses the main formatters
const formatStatValue = (value, formatter, options = {}) => {
  if (value === null || value === undefined) return '-';

  switch (formatter) {
    case 'integer':
      return formatDataString(value, 'integer');
    case 'decimal':
      return formatDataString(value, 'decimal');
    case 'percentage':
      return formatDataString(value, 'percentage');
    case 'seconds':
      return formatDataString(value, 'seconds');
    case 'time':
      return formatDataString(value, 'nanoseconds');
    case 'currency':
      const prefix = options.prefix || '';
      return `${prefix}${Math.round(value)}`;
    case 'kda':
      return value; // Handled in template
    case 'calculated':
      return value; // Handled in template
    default:
      return value.toString();
  }
};

// Get the color class based on the deviation
function getStatColorClassForCell() {
  const statKey = props.column.key;
  const stat = props.player[statKey];
  if (!stat || stat.value === null || stat.value === undefined) {
    return 'text-text-primary';
  }

  const average = stat.match_average;
  if (average === null || average === undefined) return 'text-text-primary';

  const userValue = stat.value;
  if (userValue === null || userValue === undefined) return 'text-text-primary';

  const percentageOfAverage = userValue / average;

  const isWorseWhenHigher = isWorseWhenHigherStat(statKey, props.player?.[statKey]);

  return statColor(percentageOfAverage, isWorseWhenHigher);
}

const statColor = (percentageOfAverage, isWorseWhenHigher) => {
  if (percentageOfAverage === null || percentageOfAverage === undefined) {
    return 'text-text-primary';
  }

  if (isWorseWhenHigher) {
    if (percentageOfAverage < 0.6) {
      return 'text-success-400';
    } if (percentageOfAverage < 0.8) {
      return 'text-[#9ccc70]';
    } if (percentageOfAverage > 1.4) {
      return 'text-danger-400';
    } if (percentageOfAverage > 1.2) {
      return 'text-warning-400';
    }
  } else {
    if (percentageOfAverage > 1.4) {
      return 'text-success-400';
    } if (percentageOfAverage > 1.15) {
      return 'text-[#9ccc70]';
    } if (percentageOfAverage < 0.6) {
      return 'text-danger-400';
    } if (percentageOfAverage < 0.8) {
      return 'text-yellow-400';
    }
  }

  return 'text-text-primary';
};
</script>
